import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { fetchRecentTranscripts, fetchTranscriptDetail } from '../utils/api';

import type { FirefliesTranscript } from '../types';

const CACHE_TTL_MS = 60 * 60 * 1000;

interface FirefliesState {
  transcripts: FirefliesTranscript[];
  selectedTranscript: FirefliesTranscript | null;
  transcriptDetailCache: Record<string, FirefliesTranscript>;
  lastFetchedAt: number | null;
  loading: boolean;
  error: string | null;
  fetchTranscripts: (apiKey: string, participantEmail?: string) => Promise<void>;
  forceRefresh: (apiKey: string, participantEmail?: string) => Promise<void>;
  fetchTranscriptDetail: (apiKey: string, transcriptId: string) => Promise<FirefliesTranscript | null>;
  setSelectedTranscript: (transcript: FirefliesTranscript | null) => void;
  clearError: () => void;
}

export const useFirefliesStore = create<FirefliesState>()(
  persist(
    (set, get) => ({
      transcripts: [],
      selectedTranscript: null,
      transcriptDetailCache: {},
      lastFetchedAt: null,
      loading: false,
      error: null,

      fetchTranscripts: async (apiKey: string, participantEmail?: string) => {
        const { lastFetchedAt, transcripts } = get();
        if (
          lastFetchedAt &&
          Date.now() - lastFetchedAt < CACHE_TTL_MS &&
          transcripts.length > 0
        ) {
          return;
        }
        set({ loading: true, error: null });
        try {
          const data = await fetchRecentTranscripts(apiKey, 20, participantEmail);
          set({ transcripts: data, lastFetchedAt: Date.now(), loading: false });
        } catch (err) {
          set({
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to fetch transcripts',
          });
        }
      },

      forceRefresh: async (apiKey: string, participantEmail?: string) => {
        set({ loading: true, error: null });
        try {
          const data = await fetchRecentTranscripts(apiKey, 20, participantEmail);
          set({ transcripts: data, lastFetchedAt: Date.now(), loading: false });
        } catch (err) {
          set({
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to fetch transcripts',
          });
        }
      },

      fetchTranscriptDetail: async (apiKey: string, transcriptId: string) => {
        const cached = get().transcriptDetailCache[transcriptId];
        if (cached) return cached;

        try {
          const detail = await fetchTranscriptDetail(apiKey, transcriptId);
          if (detail) {
            set((state) => ({
              transcriptDetailCache: {
                ...state.transcriptDetailCache,
                [transcriptId]: detail,
              },
            }));
          }
          return detail;
        } catch {
          return null;
        }
      },

      setSelectedTranscript: (transcript: FirefliesTranscript | null) => {
        set({ selectedTranscript: transcript });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'fireflies-store',
      partialize: (state) => ({
        transcripts: state.transcripts,
        lastFetchedAt: state.lastFetchedAt,
      }),
    },
  ),
);
